import { useContext } from 'react';
import { parse } from 'cookie';
import { identity, isNil, omit, omitBy, pick, pickBy, union } from 'lodash';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import { CookieKeys } from 'src/common/constants';
import { getDefaultSearchLocation } from 'src/common/constants/locations';
import { CountryCodes } from 'src/common/enums';
import { ROUTES } from 'src/common/routes';
import { NewSearchLocation } from 'src/global/models/NewSearchLocation';

import { getBasename } from '../../../../../ClientConfig/Selectors';
import { useLocationIdFromSlug } from '../../../../hooks/useLocationIdFromSlug';
import { SearchRouterQuery, SearchRouterQueryKey } from '../../types';
import { SEOSlugUrlParams } from '../FilterSection/context';
import { LocationQuery } from './utils';

const uniqueJobSearch = (
  arr: SearchRouterQuery[],
  props: (keyof SearchRouterQuery)[] = []
) => [
  ...new Map(
    arr.map((entry: SearchRouterQuery) => [
      props.map((k) => entry[k]).join('|'),
      entry,
    ])
  ).values(),
];

export const retrievePastSearchHistory = (
  searchCondition?: SearchRouterQuery,
  updatePastJobSearches?: (pastSearches: SearchRouterQuery[]) => void
): SearchRouterQuery[] => {
  let pastJobSearchesStored: SearchRouterQuery[] = [];

  try {
    const cookies = parse(document.cookie);
    const storedSearches = cookies[CookieKeys.pastJobSearchConditions];
    if (storedSearches) {
      pastJobSearchesStored = JSON.parse(storedSearches || '[]');
    }
  } catch (err) {
    console.error('Error fetching stored searches', err);
    pastJobSearchesStored = [];
  }

  if (searchCondition) {
    pastJobSearchesStored = union(
      [pickBy(searchCondition, identity)],
      pastJobSearchesStored
    );

    pastJobSearchesStored = uniqueJobSearch(pastJobSearchesStored, [
      SearchRouterQueryKey.Keyword,
      SearchRouterQueryKey.Country,
      SearchRouterQueryKey.Location,
    ]);

    if (pastJobSearchesStored.length >= 3) {
      pastJobSearchesStored = pastJobSearchesStored.slice(0, 3);
    }

    if (updatePastJobSearches) {
      updatePastJobSearches(pastJobSearchesStored);
    }
  }

  return pastJobSearchesStored;
};

export function createLocationQuery({
  locationResponse,
  searchLocation,
  selectedCountry,
  location,
  pathname,
  locationId,
}: {
  locationResponse: NewSearchLocation[];
  searchLocation: NewSearchLocation | undefined;
  selectedCountry: CountryCodes;
  location: LocationQuery;
  pathname: string;
  locationId: string;
}): LocationQuery {
  const defaultSearch = getDefaultSearchLocation(selectedCountry);

  // If new location is entered in the location search field.
  const locationHasChanged =
    location[SearchRouterQueryKey.Location] !== searchLocation?.label;
  const countryHasChanged =
    location[SearchRouterQueryKey.Country] !== selectedCountry;

  if (searchLocation && (locationHasChanged || countryHasChanged)) {
    const response =
      locationResponse.find((res) => res.label === searchLocation.label) ??
      searchLocation;

    if (pathname.match(/^\/job-location\/([^/]+)/)) {
      return {
        [SearchRouterQueryKey.Country]: selectedCountry,
        [SearchRouterQueryKey.LocationId]: locationId,
        [SearchRouterQueryKey.Location]: searchLocation.label,
        ...(searchLocation.hierarchicalLocation && {
          [SearchRouterQueryKey.LowestLocationlevel]:
            searchLocation.hierarchicalLocation.level.toString(),
        }),
      };
    } else if (searchLocation.label === defaultSearch.name) {
      /** if all cities/provinces */
      return {
        [SearchRouterQueryKey.Country]: selectedCountry,
        [SearchRouterQueryKey.Location]: defaultSearch.name,
        [SearchRouterQueryKey.LowestLocationlevel]: '1',
      };
    } else if (response.defaultLocation) {
      return {
        [SearchRouterQueryKey.Country]: response.defaultLocation.CountryCode,
        [SearchRouterQueryKey.Location]: response.label,
        ...(response.defaultLocation.id && {
          [SearchRouterQueryKey.LocationId]:
            response.defaultLocation.id.toString(),
        }),
        ...(response.hierarchicalLocation && {
          [SearchRouterQueryKey.LowestLocationlevel]:
            response.hierarchicalLocation.level.toString(),
        }),
      };
    } else if (
      response.isHierarchicalLocation &&
      response.hierarchicalLocation
    ) {
      const { hierarchicalLocation } = response;
      return {
        [SearchRouterQueryKey.Country]:
          hierarchicalLocation.CountryCode ?? selectedCountry,
        [SearchRouterQueryKey.LocationId]: hierarchicalLocation.id,
        [SearchRouterQueryKey.Location]: response.label,
        [SearchRouterQueryKey.LowestLocationlevel]:
          hierarchicalLocation.level.toString(),
      };
    } else if (response.legacySearchLocation) {
      return {
        [SearchRouterQueryKey.Country]:
          response.legacySearchLocation.links?.country?.code,
        // @ts-ignore Optional chaining in city id because, when only country is entered in the search field, City property is null.
        [SearchRouterQueryKey.City]: response.legacySearchLocation?.id,
        [SearchRouterQueryKey.Location]: response.label,
      };
    }
  }

  return {
    ...location,
    [SearchRouterQueryKey.Country]: selectedCountry,
    [SearchRouterQueryKey.Location]: defaultSearch.name,
    [SearchRouterQueryKey.LowestLocationlevel]: '1',
    ...(defaultSearch.id && {
      [SearchRouterQueryKey.LocationId]: defaultSearch.id,
    }),
  };
}

export function useGenerateLocationQuery(
  locationResponse: NewSearchLocation[],
  searchLocation: NewSearchLocation | undefined,
  selectedCountry: CountryCodes
) {
  const router = useRouter();
  const { slugQuery } = useContext(SEOSlugUrlParams);
  const { locationId: _locationId } = slugQuery || {};

  const basename = useSelector(getBasename);
  const pathname = basename
    ? router.asPath.replace(basename, '')
    : router.asPath;
  const locationId = useLocationIdFromSlug(pathname, _locationId);

  const location = pick(
    router.query,
    omit(Object.values(SearchRouterQueryKey), [SearchRouterQueryKey.Keyword])
  ) as LocationQuery;

  return createLocationQuery({
    locationResponse,
    searchLocation,
    selectedCountry,
    location,
    pathname,
    locationId,
  });
}

export function generateSearchQuery(
  keyword: string | undefined,
  locationQuery: LocationQuery,
  updatePastJobSearches?: (pastSearches: SearchRouterQuery[]) => void,
  marketingURLParams?: Record<string, string>,
  isMobile = false,
  onClose?: () => void
) {
  const query = omitBy(
    {
      [SearchRouterQueryKey.Keyword]: keyword ? keyword : null,
      ...locationQuery,
    },
    isNil
  );

  if (
    query &&
    (query[SearchRouterQueryKey.Keyword] ||
      query[SearchRouterQueryKey.Location])
  ) {
    retrievePastSearchHistory(query, updatePastJobSearches);
  }

  if (isMobile) {
    onClose();
  }

  return {
    pathname: `/${ROUTES.opportunitiesJobsExplore}`,
    query: {
      ...query,
      ...marketingURLParams,
    },
  };
}
